import { useState, useMemo, useEffect, Fragment } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useVendorMerge } from "@/hooks/useVendorMerge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesAlert } from "@/components/shared/UnsavedChangesAlert";
import { toast } from "sonner";
import type { Vendor } from "@/hooks/useVendors";
import { GitMerge, AlertTriangle, Check } from "lucide-react";

type FieldDef = {
  key: string;
  label: string;
  format?: (v: any) => string;
};

const fmtBool = (v: any) => (v === true ? "Yes" : v === false ? "No" : "—");
const fmtDate = (v: any) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return String(v);
  }
};
const fmtMoney = (v: any) =>
  v == null || v === "" ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
const fmtPct = (v: any) => (v == null || v === "" ? "—" : `${Number(v)}%`);
const fmtArr = (v: any) => (Array.isArray(v) && v.length ? v.join(", ") : "—");
const fmt = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));

const FIELD_GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "Identity",
    fields: [
      { key: "name", label: "Vendor Name" },
      { key: "vendor_number", label: "Vendor #" },
      { key: "vendor_types", label: "Vendor Types", format: fmtArr },
      { key: "compliance_status", label: "Compliance Status" },
    ],
  },
  {
    title: "Contact",
    fields: [
      { key: "contact_name", label: "Contact Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
    ],
  },
  {
    title: "Billing Address",
    fields: [
      { key: "billing_address", label: "Address" },
      { key: "billing_city", label: "City" },
      { key: "billing_state", label: "State" },
      { key: "billing_zip", label: "Zip" },
    ],
  },
  {
    title: "Tax & 1099",
    fields: [
      { key: "ein", label: "EIN" },
      { key: "w9_on_file", label: "W-9 on file", format: fmtBool },
      { key: "w9_received_date", label: "W-9 Received", format: fmtDate },
      { key: "is_1099_eligible", label: "1099 eligible", format: fmtBool },
    ],
  },
  {
    title: "Insurance & License",
    fields: [
      { key: "insurance_carrier", label: "Insurance Carrier" },
      { key: "insurance_expiry", label: "Insurance Expiry", format: fmtDate },
      { key: "license_number", label: "License #" },
      { key: "license_state", label: "License State" },
      { key: "license_type", label: "License Type" },
      { key: "license_expiry", label: "License Expiry", format: fmtDate },
      { key: "lien_waiver_required", label: "Lien Waiver Req.", format: fmtBool },
    ],
  },
  {
    title: "Accounting",
    fields: [
      { key: "payment_terms", label: "Payment Terms" },
      { key: "payment_terms_custom_days", label: "Custom Net Days" },
      { key: "account_number", label: "Account #" },
      { key: "credit_limit", label: "Credit Limit", format: fmtMoney },
      { key: "default_retainage_pct", label: "Default Retainage", format: fmtPct },
    ],
  },
  {
    title: "QuickBooks",
    fields: [
      { key: "qb_vendor_id", label: "QB Vendor ID" },
      { key: "qb_sync_status", label: "QB Sync Status" },
      { key: "qb_synced_at", label: "QB Last Synced", format: fmtDate },
    ],
  },
  {
    title: "Metadata",
    fields: [
      { key: "created_at", label: "Created", format: fmtDate },
      { key: "onboarding_notes", label: "Onboarding Notes" },
    ],
  },
  {
    title: "Notes",
    fields: [{ key: "notes", label: "General Notes" }],
  },
];

const ALL_FIELDS = FIELD_GROUPS.flatMap((g) => g.fields);

interface ImpactCounts {
  work_orders: number;
  purchase_orders: number;
  bills: number;
  expenses: number;
  notes: number;
  documents: number;
}

async function fetchImpactCounts(vendorId: string): Promise<ImpactCounts> {
  const [wos, pos, bills, expenses, notes, docs] = await Promise.all([
    (supabase.from("work_orders") as any).select("id", { count: "exact", head: true }).eq("contractor_id", vendorId),
    (supabase.from("purchase_orders") as any).select("id", { count: "exact", head: true }).eq("vendor_id", vendorId),
    (supabase.from("bills") as any).select("id", { count: "exact", head: true }).eq("vendor_id", vendorId),
    (supabase.from("project_expenses") as any).select("id", { count: "exact", head: true }).eq("vendor_id", vendorId),
    (supabase.from("vendor_notes") as any).select("id", { count: "exact", head: true }).eq("vendor_id", vendorId),
    (supabase.from("vendor_documents") as any).select("id", { count: "exact", head: true }).eq("vendor_id", vendorId),
  ]);
  return {
    work_orders: wos.count ?? 0,
    purchase_orders: pos.count ?? 0,
    bills: bills.count ?? 0,
    expenses: expenses.count ?? 0,
    notes: notes.count ?? 0,
    documents: docs.count ?? 0,
  };
}

function total(c: ImpactCounts) {
  return c.work_orders + c.purchase_orders + c.bills + c.expenses + c.notes + c.documents;
}

function CountsBadge({ counts }: { counts: ImpactCounts | undefined }) {
  if (!counts) return null;
  const parts: string[] = [];
  if (counts.work_orders) parts.push(`${counts.work_orders} WO`);
  if (counts.purchase_orders) parts.push(`${counts.purchase_orders} PO`);
  if (counts.bills) parts.push(`${counts.bills} bill`);
  if (counts.expenses) parts.push(`${counts.expenses} exp`);
  if (counts.notes) parts.push(`${counts.notes} note`);
  if (counts.documents) parts.push(`${counts.documents} doc`);
  if (parts.length === 0) return <span className="text-xs text-muted-foreground ml-1">(no records)</span>;
  return <span className="text-xs text-muted-foreground ml-1">({parts.join(", ")})</span>;
}

function formatValue(field: FieldDef, vendor: Vendor): string {
  const raw = (vendor as any)[field.key];
  if (field.format) return field.format(raw);
  return fmt(raw);
}

interface VendorMergeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorA: Vendor;
  vendorB: Vendor;
}

export const VendorMergeDialog = ({ open, onOpenChange, vendorA, vendorB }: VendorMergeDialogProps) => {
  const { user } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const merge = useVendorMerge();

  const { data: impactBoth } = useQuery({
    queryKey: ["vendor_merge_impact_both", vendorA.id, vendorB.id],
    queryFn: async () => {
      const [a, b] = await Promise.all([fetchImpactCounts(vendorA.id), fetchImpactCounts(vendorB.id)]);
      return { a, b };
    },
    enabled: open && !!user,
  });

  const recommendedId = useMemo(() => {
    if (!impactBoth) return vendorA.id;
    return total(impactBoth.b) > total(impactBoth.a) ? vendorB.id : vendorA.id;
  }, [impactBoth, vendorA.id, vendorB.id]);

  const [winnerId, setWinnerId] = useState(vendorA.id);
  const [fieldChoices, setFieldChoices] = useState<Record<string, "a" | "b">>(() => {
    const defaults: Record<string, "a" | "b"> = {};
    for (const f of ALL_FIELDS) defaults[f.key] = "a";
    return defaults;
  });

  useEffect(() => {
    if (impactBoth) setWinnerId(recommendedId);
  }, [recommendedId, impactBoth]);

  // Reset state when dialog opens for a fresh vendor pair
  useEffect(() => {
    if (open) {
      const defaults: Record<string, "a" | "b"> = {};
      for (const f of ALL_FIELDS) defaults[f.key] = "a";
      setFieldChoices(defaults);
    }
  }, [open, vendorA.id, vendorB.id]);

  const loserId = winnerId === vendorA.id ? vendorB.id : vendorA.id;
  const winner = winnerId === vendorA.id ? vendorA : vendorB;
  const loser = winnerId === vendorA.id ? vendorB : vendorA;
  const loserCounts = impactBoth ? (loserId === vendorA.id ? impactBoth.a : impactBoth.b) : undefined;
  const winnerCounts = impactBoth ? (winnerId === vendorA.id ? impactBoth.a : impactBoth.b) : undefined;

  const showDirectionWarning = useMemo(() => {
    if (!impactBoth || !loserCounts || !winnerCounts) return false;
    return winnerId !== recommendedId && total(loserCounts) > total(winnerCounts);
  }, [winnerId, recommendedId, impactBoth, loserCounts, winnerCounts]);

  // Dirty if user touched any choice from default
  const isDirty = useMemo(() => {
    if (winnerId !== vendorA.id) return true;
    for (const f of ALL_FIELDS) {
      if (fieldChoices[f.key] !== "a") return true;
    }
    return false;
  }, [winnerId, fieldChoices, vendorA.id]);

  const overrides = useMemo(() => {
    const result: Record<string, unknown> = {};
    for (const f of ALL_FIELDS) {
      const chosen = fieldChoices[f.key] === "a" ? vendorA : vendorB;
      if (chosen.id !== winnerId) {
        const raw = (chosen as any)[f.key];
        if (raw !== undefined) result[f.key] = raw;
      }
    }
    return result;
  }, [fieldChoices, winnerId, vendorA, vendorB]);

  const guard = useUnsavedChangesGuard(isDirty && !confirmOpen, () => onOpenChange(false));

  const handleMerge = async () => {
    try {
      await merge.mutateAsync({ winnerId, loserId, overrides });
      toast.success(`Merged into ${winner.name}`);
      setConfirmOpen(false);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Merge failed");
    }
  };

  // Warnings
  const einA = (vendorA.ein || "").trim();
  const einB = (vendorB.ein || "").trim();
  const einMismatch = !!(einA && einB && einA !== einB);
  const qbWarning = !!(vendorA.qb_vendor_id || vendorB.qb_vendor_id);

  return (
    <TooltipProvider delayDuration={300}>
      <Dialog open={open} onOpenChange={guard.guardOpenChange(onOpenChange)}>
        <DialogContent
          dirty={isDirty}
          onRequestClose={guard.requestClose}
          className="w-[95vw] max-w-[1400px] h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0 sm:rounded-lg"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-border shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <GitMerge className="h-5 w-5 shrink-0" /> Merge Vendors
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Choose which vendor to keep. The other will be archived and all its records moved.
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable middle */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            {/* Winner picker */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                Keep which vendor?
              </Label>
              <RadioGroup value={winnerId} onValueChange={setWinnerId} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[vendorA, vendorB].map((v, i) => {
                  const isWinner = winnerId === v.id;
                  const isRecommended = impactBoth && recommendedId === v.id;
                  return (
                    <label
                      key={v.id}
                      className={`relative flex flex-col gap-1 border rounded-lg px-3 py-2.5 cursor-pointer transition-all ${
                        isWinner ? "border-primary bg-primary/10 ring-1 ring-primary/20" : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value={v.id} className="shrink-0" />
                        <span className="text-sm font-medium text-foreground break-words flex-1">
                          #{v.vendor_number ?? "—"} {v.name}
                        </span>
                        {isWinner && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 pl-6 flex-wrap">
                        <CountsBadge counts={i === 0 ? impactBoth?.a : impactBoth?.b} />
                        {isRecommended && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Recommended</Badge>
                        )}
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Warning strip */}
            {(showDirectionWarning || einMismatch || qbWarning) && (
              <div className="space-y-2">
                {showDirectionWarning && loserCounts && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-xs sm:text-sm text-foreground">
                      <strong>Warning:</strong> You're keeping the vendor with fewer linked items.{" "}
                      <strong>#{loser.vendor_number} {loser.name}</strong> has more records that will be reassigned.
                    </p>
                  </div>
                )}
                {einMismatch && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-xs sm:text-sm text-foreground">
                      <strong>EIN mismatch:</strong> These vendors have different EINs ({einA} vs {einB}). Only merge if they
                      are the same legal entity — otherwise 1099 reporting will be incorrect.
                    </p>
                  </div>
                )}
                {qbWarning && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <p className="text-xs sm:text-sm text-foreground">
                      <strong>QuickBooks:</strong> At least one vendor is linked to QuickBooks. The merge will not auto-merge
                      QuickBooks vendors — perform the equivalent merge in QuickBooks separately.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Field comparison */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Choose field values
                </Label>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Switch checked={showEmpty} onCheckedChange={setShowEmpty} />
                  Show empty fields
                </label>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead className="bg-muted">
                      <tr>
                        <th className="sticky left-0 z-20 bg-muted text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground w-48 min-w-48">
                          Field
                        </th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground min-w-[320px]">
                          <div className="break-words">#{vendorA.vendor_number} {vendorA.name}</div>
                        </th>
                        <th className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground min-w-[320px]">
                          <div className="break-words">#{vendorB.vendor_number} {vendorB.name}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {FIELD_GROUPS.map((group) => {
                        const visibleFields = group.fields.filter((f) => {
                          if (showEmpty) return true;
                          const a = formatValue(f, vendorA);
                          const b = formatValue(f, vendorB);
                          return !(a === "—" && b === "—");
                        });
                        if (visibleFields.length === 0) return null;
                        return (
                          <Fragment key={group.title}>
                            <tr key={`hdr-${group.title}`} className="bg-muted/40">
                              <td
                                colSpan={3}
                                className="sticky left-0 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40"
                              >
                                {group.title}
                              </td>
                            </tr>
                            {visibleFields.map((f) => {
                              const valA = formatValue(f, vendorA);
                              const valB = formatValue(f, vendorB);
                              const isDifferent = valA !== valB;
                              const choiceA = fieldChoices[f.key] === "a";
                              const choiceB = fieldChoices[f.key] === "b";
                              const rowBg = isDifferent ? "bg-primary/5" : "";
                              const stickyBg = isDifferent ? "bg-[hsl(var(--primary)/0.05)]" : "bg-background";
                              return (
                                <tr key={f.key} className={`border-t ${rowBg}`}>
                                  <td className={`sticky left-0 z-10 px-3 py-2 align-top text-xs font-medium text-muted-foreground w-48 min-w-48 ${stickyBg}`}>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span>{f.label}</span>
                                      {isDifferent && (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary/40 text-primary">
                                          differs
                                        </Badge>
                                      )}
                                    </div>
                                  </td>
                                  {[
                                    { side: "a" as const, val: valA, checked: choiceA },
                                    { side: "b" as const, val: valB, checked: choiceB },
                                  ].map(({ side, val, checked }) => (
                                    <td key={side} className="px-3 py-2 align-top">
                                      <label
                                        className={`flex items-start gap-2 cursor-pointer rounded px-2 py-1.5 -mx-1 transition-colors ${
                                          checked ? "bg-primary/10" : "hover:bg-muted/40"
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={f.key}
                                          checked={checked}
                                          onChange={() => setFieldChoices((p) => ({ ...p, [f.key]: side }))}
                                          className="accent-primary shrink-0 mt-1"
                                        />
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="text-sm text-foreground whitespace-pre-wrap break-words leading-snug">
                                              {val}
                                            </span>
                                          </TooltipTrigger>
                                          {val !== "—" && val.length > 40 && (
                                            <TooltipContent className="max-w-md whitespace-pre-wrap break-words">
                                              {val}
                                            </TooltipContent>
                                          )}
                                        </Tooltip>
                                      </label>
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Impact summary */}
            {loserCounts && total(loserCounts) > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-muted border border-border">
                <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="text-xs sm:text-sm text-foreground">
                  <p className="font-medium">Will be moved to {winner.name}:</p>
                  <ul className="mt-1 text-muted-foreground list-disc ml-4">
                    {loserCounts.work_orders > 0 && <li>{loserCounts.work_orders} work order(s)</li>}
                    {loserCounts.purchase_orders > 0 && <li>{loserCounts.purchase_orders} purchase order(s)</li>}
                    {loserCounts.bills > 0 && <li>{loserCounts.bills} bill(s)</li>}
                    {loserCounts.expenses > 0 && <li>{loserCounts.expenses} expense(s)</li>}
                    {loserCounts.notes > 0 && <li>{loserCounts.notes} note(s)</li>}
                    {loserCounts.documents > 0 && <li>{loserCounts.documents} document(s)</li>}
                  </ul>
                  <p className="mt-1 text-muted-foreground">
                    <strong>#{loser.vendor_number} {loser.name}</strong> will be archived.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Fixed footer */}
          <div className="flex justify-end gap-2 px-6 py-3 border-t border-border shrink-0 bg-background">
            <Button variant="outline" onClick={guard.requestClose}>Cancel</Button>
            <Button onClick={() => setConfirmOpen(true)} disabled={merge.isPending}>
              <GitMerge className="h-4 w-4 mr-1" /> Merge Vendors
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <UnsavedChangesAlert
        open={guard.confirmOpen}
        onKeepEditing={guard.keepEditing}
        onDiscard={guard.confirmDiscard}
      />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm vendor merge</AlertDialogTitle>
            <AlertDialogDescription>
              All records from <strong>#{loser.vendor_number} {loser.name}</strong> will be moved to{" "}
              <strong>#{winner.vendor_number} {winner.name}</strong>, and the loser will be archived.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMerge} disabled={merge.isPending}>
              {merge.isPending ? "Merging…" : "Confirm Merge"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};
